import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppNav } from "@/components/AppNav";
import { BrBox } from "@/components/BrBox";
import { BrBtn } from "@/components/BrBtn";
import { BrLabel } from "@/components/BrLabel";
import { BrSlider } from "@/components/BrSlider";
import { TcIcon } from "@/components/TcIcon";
import {
  useCreateEndless,
  useCreateTrainingSession,
  useRoutines,
  useRunRoutine,
  TierRequiredError,
  offsetShadow,
  TYPE,
  ALL_OPENINGS,
  ALL_SITUATIONS,
  SITUATION_LABEL,
  TIME_CONTROLS,
  type RoutineOut,
} from "@chess/shared";
import { BrTag } from "@/components/BrTag";
import { useToast } from "@/components/ToastProvider";
import { useTheme } from "@/theme/ThemeProvider";
import { useViewport } from "@/util/viewport";

interface Props {
  onOpenSettings: () => void;
  onOpenBuilder: (initial?: RoutineOut | null) => void;
  onOpenUpgrade: () => void;
}

function scheduleLabel(r: RoutineOut): string {
  if (r.schedule_kind === "daily") return `Daily${r.schedule_time_local ? ` · ${r.schedule_time_local}` : ""}`;
  if (r.schedule_kind === "weekdays") {
    const names = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    return r.schedule_weekdays.map((d) => names[d]).join(" · ") || "Weekly";
  }
  if (r.schedule_kind === "days_per_week") return `${r.schedule_days_per_week ?? "?"}× / week`;
  return "Manual";
}

export function TrainingSelectPage({ onOpenSettings, onOpenBuilder, onOpenUpgrade }: Props) {
  const p = useTheme();
  const { isMobile } = useViewport();
  const nav = useNavigate();
  const toast = useToast();
  const createSession = useCreateTrainingSession();
  const createEndless = useCreateEndless();
  const routines = useRoutines();
  const runRoutine = useRunRoutine();
  const [picked, setPicked] = useState<string[]>(["Forks", "Pins"]);
  // Fast-choice quick-start config.
  const [choiceDifficulty, setChoiceDifficulty] = useState<"easy" | "medium" | "hard">("medium");
  const [choiceSpeed, setChoiceSpeed] = useState<"bullet" | "blitz">("blitz");
  const [swipeVariant, setSwipeVariant] = useState<"judge" | "duel">("judge");
  const [swipeDifficulty, setSwipeDifficulty] = useState<"easy" | "hard">("easy");
  const [swipeSeconds, setSwipeSeconds] = useState(5);
  const [swipeLengthMode, setSwipeLengthMode] = useState<"cards" | "minutes">("cards");
  const [swipeCards, setSwipeCards] = useState<10 | 20 | 30>(20);
  const [swipeMinutes, setSwipeMinutes] = useState<3 | 5 | 10>(5);

  async function startRoutine(id: string) {
    try {
      const ts = await runRoutine.mutateAsync(id);
      if (ts.items.length === 0) {
        toast.push({ kind: "info", text: "No matching positions yet — import more games." });
        return;
      }
      nav(`/app/train/${ts.id}`);
    } catch (err) {
      if (err instanceof TierRequiredError) {
        toast.push({ kind: "danger", text: "Pro feature — upgrade to use this." });
        onOpenUpgrade();
      } else {
        toast.push({ kind: "danger", text: err instanceof Error ? err.message : "Failed to start routine" });
      }
    }
  }

  function toggle(s: string) {
    setPicked((arr) => (arr.includes(s) ? arr.filter((x) => x !== s) : [...arr, s]));
  }

  async function startSession(minutes = 12) {
    try {
      const ts = await createSession.mutateAsync({ target_minutes: minutes, format: "tactics" });
      if (ts.items.length === 0) {
        toast.push({ kind: "danger", text: "No mistakes to drill yet — import & evaluate some games first." });
        return;
      }
      nav(`/app/train/${ts.id}`);
    } catch (err) {
      toast.push({ kind: "danger", text: err instanceof Error ? err.message : "Failed to create session" });
    }
  }

  async function startEndless() {
    try {
      const ts = await createEndless.mutateAsync();
      if (ts.items.length === 0) {
        toast.push({ kind: "danger", text: "No mistakes to drill yet — import & evaluate some games first." });
        return;
      }
      nav(`/app/train/${ts.id}?endless=1`);
    } catch (err) {
      toast.push({ kind: "danger", text: err instanceof Error ? err.message : "Failed to start endless" });
    }
  }

  async function startChoice() {
    try {
      const ts = await createSession.mutateAsync({
        target_minutes: 12,
        format: "mixed",
        mode: "choice",
        choice_difficulty: choiceDifficulty,
        choice_seconds: choiceSpeed === "bullet" ? 5 : 15,
      });
      if (ts.items.length === 0) {
        toast.push({ kind: "danger", text: "Not enough analysed mistakes for choice drills yet — import & evaluate more games." });
        return;
      }
      nav(`/app/train/${ts.id}`);
    } catch (err) {
      toast.push({ kind: "danger", text: err instanceof Error ? err.message : "Failed to start fast choice" });
    }
  }

  async function startSwipe() {
    try {
      const ts = await createSession.mutateAsync({
        target_minutes: swipeLengthMode === "minutes" ? swipeMinutes : 12,
        format: "mixed",
        mode: "swipe",
        swipe_variant: swipeVariant,
        swipe_difficulty: swipeDifficulty,
        swipe_seconds: swipeSeconds,
        card_count: swipeLengthMode === "cards" ? swipeCards : null,
      });
      if (ts.items.length === 0) {
        toast.push({ kind: "danger", text: "Not enough analysed mistakes for swipe drills yet — import & evaluate more games." });
        return;
      }
      nav(`/app/train/${ts.id}`);
    } catch (err) {
      toast.push({ kind: "danger", text: err instanceof Error ? err.message : "Failed to start swipe" });
    }
  }

  const Chip = ({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) => (
    <button
      onClick={onClick}
      style={{
        padding: "8px 14px",
        fontSize: 12.5,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        fontFamily: TYPE.body,
        background: active ? p.accent : p.card,
        color: active ? p.accentFg : p.fg,
        border: `2px solid ${p.border}`,
        boxShadow: offsetShadow(p, 2, 2),
        cursor: "pointer",
      }}
    >
      {active && <span style={{ marginRight: 6 }}>✓</span>}
      {label}
    </button>
  );

  return (
    <div style={{ minHeight: "100vh", background: p.bg, color: p.fg, fontFamily: TYPE.body }}>
      <AppNav onOpenSettings={onOpenSettings} />
      <main
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: isMobile ? "22px 16px 56px" : "36px 32px 72px",
          display: "flex",
          flexDirection: "column",
          gap: 32,
        }}
      >
        <div>
          <BrLabel>BUILD YOUR SESSION</BrLabel>
          <h1
            style={{
              fontFamily: TYPE.display,
              fontSize: isMobile ? 40 : 60,
              fontWeight: 800,
              letterSpacing: isMobile ? -1.5 : -2.5,
              lineHeight: 0.95,
              margin: "6px 0 12px",
              textTransform: "uppercase",
            }}
          >
            WHAT DO YOU<br />WANT TO TRAIN?
          </h1>
          <p style={{ fontSize: isMobile ? 14 : 16, color: p.fgMuted, lineHeight: 1.5, maxWidth: 600, margin: 0, fontWeight: 500 }}>
            Quick-start a format, run a saved daily routine, or build a session from specific patterns.
          </p>
        </div>

        <BrBox bg={p.fg} fg={p.bg} padding={isMobile ? 24 : 36} shadowSize={isMobile ? 6 : 8}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr auto",
              gap: 24,
              alignItems: "center",
            }}
          >
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                <span style={{ color: p.accent, display: "inline-flex", alignItems: "center" }}>
                  <TcIcon name="endless" size={isMobile ? 48 : 64} />
                </span>
                <div>
                  <BrLabel color={p.bgAlt}>NO CLOCK · OPEN ENDED</BrLabel>
                  <div
                    style={{
                      fontFamily: TYPE.display,
                      fontSize: isMobile ? 32 : 44,
                      fontWeight: 800,
                      letterSpacing: -1.5,
                      lineHeight: 1,
                      textTransform: "uppercase",
                      marginTop: 4,
                    }}
                  >
                    ENDLESS TRAINING
                  </div>
                </div>
              </div>
              <p style={{ fontSize: 14, color: p.bgAlt, lineHeight: 1.55, margin: "8px 0 0", maxWidth: 560, fontWeight: 500 }}>
                Stockfish keeps feeding you positions from your blunder bank until you stop. No clock, no streak break — just pure deliberate practice.
              </p>
            </div>
            <BrBtn variant="accent" size="lg" onClick={() => startEndless()} disabled={createEndless.isPending}>
              {createEndless.isPending ? "Working…" : "Start endless →"}
            </BrBtn>
          </div>
        </BrBox>

        {/* Fast choice quick-start */}
        <BrBox padding={isMobile ? 22 : 32} shadowSize={isMobile ? 6 : 8}>
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <div>
              <BrLabel>TIMED · MULTIPLE CHOICE</BrLabel>
              <div style={{ fontFamily: TYPE.display, fontSize: isMobile ? 30 : 44, fontWeight: 800, letterSpacing: -1.5, lineHeight: 1, textTransform: "uppercase", marginTop: 4 }}>
                FAST CHOICE
              </div>
              <p style={{ fontSize: 14, color: p.fgMuted, lineHeight: 1.55, margin: "8px 0 0", maxWidth: 560, fontWeight: 500 }}>
                Three moves flash on the board. Pick the best before the clock runs out. Train pattern recognition at speed.
              </p>
            </div>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 10, fontFamily: TYPE.mono, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: p.fgMuted, marginBottom: 6 }}>DIFFICULTY</div>
                <div style={{ display: "flex", gap: 6 }}>
                  {(["easy", "medium", "hard"] as const).map((d) => (
                    <Chip key={d} label={d} active={choiceDifficulty === d} onClick={() => setChoiceDifficulty(d)} />
                  ))}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, fontFamily: TYPE.mono, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: p.fgMuted, marginBottom: 6 }}>SPEED</div>
                <div style={{ display: "flex", gap: 6 }}>
                  <Chip label="Bullet · 5s" active={choiceSpeed === "bullet"} onClick={() => setChoiceSpeed("bullet")} />
                  <Chip label="Blitz · 15s" active={choiceSpeed === "blitz"} onClick={() => setChoiceSpeed("blitz")} />
                </div>
              </div>
            </div>
            <div>
              <BrBtn variant="accent" size="lg" onClick={() => startChoice()} disabled={createSession.isPending}>
                {createSession.isPending ? "Working…" : "Start fast choice →"}
              </BrBtn>
            </div>
          </div>
        </BrBox>

        {/* Swipe quick-start */}
        <BrBox padding={isMobile ? 22 : 32} shadowSize={isMobile ? 6 : 8}>
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <div>
              <BrLabel>TIMED · CARD STACK</BrLabel>
              <div style={{ fontFamily: TYPE.display, fontSize: isMobile ? 30 : 44, fontWeight: 800, letterSpacing: -1.5, lineHeight: 1, textTransform: "uppercase", marginTop: 4 }}>
                SWIPE
              </div>
              <p style={{ fontSize: 14, color: p.fgMuted, lineHeight: 1.55, margin: "8px 0 0", maxWidth: 560, fontWeight: 500 }}>
                {swipeVariant === "judge"
                  ? "One move per card — swipe right if it's the best move, left if it's not. Built for bullet-speed instincts."
                  : "Two moves per card — swipe toward the better one before the clock runs out. Built for bullet-speed instincts."}
              </p>
            </div>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 10, fontFamily: TYPE.mono, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: p.fgMuted, marginBottom: 6 }}>VARIANT</div>
                <div style={{ display: "flex", gap: 6 }}>
                  <Chip label="Judge · 1 move" active={swipeVariant === "judge"} onClick={() => setSwipeVariant("judge")} />
                  <Chip label="Duel · 2 moves" active={swipeVariant === "duel"} onClick={() => setSwipeVariant("duel")} />
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, fontFamily: TYPE.mono, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: p.fgMuted, marginBottom: 6 }}>DIFFICULTY</div>
                <div style={{ display: "flex", gap: 6 }}>
                  {(["easy", "hard"] as const).map((d) => (
                    <Chip key={d} label={d} active={swipeDifficulty === d} onClick={() => setSwipeDifficulty(d)} />
                  ))}
                </div>
              </div>
              <div style={{ minWidth: isMobile ? 200 : 240 }}>
                <div style={{ fontSize: 10, fontFamily: TYPE.mono, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: p.fgMuted, marginBottom: 6 }}>TIME TO SWIPE</div>
                <BrSlider min={2} max={15} value={swipeSeconds} onChange={setSwipeSeconds} format={(n) => `${n}s`} />
              </div>
              <div>
                <div style={{ fontSize: 10, fontFamily: TYPE.mono, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: p.fgMuted, marginBottom: 6 }}>LENGTH</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <Chip label="Cards" active={swipeLengthMode === "cards"} onClick={() => setSwipeLengthMode("cards")} />
                  <Chip label="Minutes" active={swipeLengthMode === "minutes"} onClick={() => setSwipeLengthMode("minutes")} />
                  {swipeLengthMode === "cards"
                    ? ([10, 20, 30] as const).map((n) => (
                        <Chip key={n} label={`${n}`} active={swipeCards === n} onClick={() => setSwipeCards(n)} />
                      ))
                    : ([3, 5, 10] as const).map((n) => (
                        <Chip key={n} label={`${n} min`} active={swipeMinutes === n} onClick={() => setSwipeMinutes(n)} />
                      ))}
                </div>
              </div>
            </div>
            <div>
              <BrBtn variant="accent" size="lg" onClick={() => startSwipe()} disabled={createSession.isPending}>
                {createSession.isPending ? "Working…" : "Start swipe →"}
              </BrBtn>
            </div>
          </div>
        </BrBox>

        <div>
          <BrLabel>BY TIME CONTROL</BrLabel>
          <h2
            style={{
              fontFamily: TYPE.display,
              fontSize: isMobile ? 28 : 32,
              fontWeight: 800,
              letterSpacing: -0.8,
              textTransform: "uppercase",
              margin: "6px 0 14px",
            }}
          >
            Train the clock you play.
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)",
              gap: isMobile ? 10 : 16,
            }}
          >
            {TIME_CONTROLS.filter((t) => t.id !== "none").map((tc, i) => {
              const accent = i === 1;
              return (
                <button
                  key={tc.id}
                  onClick={() => startSession()}
                  style={{
                    background: accent ? p.accent : p.card,
                    color: accent ? p.accentFg : p.fg,
                    border: `2px solid ${p.border}`,
                    boxShadow: offsetShadow(p, 4, 4),
                    padding: isMobile ? "18px 16px" : "24px 20px",
                    cursor: "pointer",
                    fontFamily: TYPE.body,
                    textAlign: "left",
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    minHeight: 140,
                  }}
                >
                  <TcIcon name={tc.icon} size={36} />
                  <div style={{ fontFamily: TYPE.display, fontSize: 26, fontWeight: 800, textTransform: "uppercase", letterSpacing: -0.5, marginTop: 8 }}>{tc.label}</div>
                  <div style={{ fontSize: 12, fontFamily: TYPE.mono, fontWeight: 700, letterSpacing: 0.5, opacity: 0.75 }}>{tc.detail}</div>
                  <div style={{ flex: 1 }} />
                  <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1, marginTop: 8 }}>Start →</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Your routines */}
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
            <div>
              <BrLabel>YOUR ROUTINES</BrLabel>
              <h2 style={{ fontFamily: TYPE.display, fontSize: isMobile ? 28 : 32, fontWeight: 800, letterSpacing: -0.8, textTransform: "uppercase", margin: "6px 0 0" }}>
                Custom daily plans.
              </h2>
            </div>
            <BrBtn variant="primary" size="md" onClick={() => onOpenBuilder(null)}>+ New routine</BrBtn>
          </div>
          {routines.data && routines.data.length > 0 ? (
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 14 }}>
              {routines.data.map((r, i) => (
                <BrBox key={r.id} padding={isMobile ? 18 : 22} shadowSize={5}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
                    <div>
                      <div style={{ fontFamily: TYPE.display, fontSize: isMobile ? 22 : 26, fontWeight: 800, letterSpacing: -0.6, textTransform: "uppercase", lineHeight: 1.05 }}>{r.name}</div>
                      <div style={{ fontSize: 11, fontFamily: TYPE.mono, fontWeight: 700, letterSpacing: 0.5, marginTop: 4, color: p.fgMuted, textTransform: "uppercase" }}>{scheduleLabel(r)}</div>
                      {r.paused_by_tier && (
                        <BrTag bg={p.card} fg={p.fgMuted} style={{ marginTop: 6 }}>PAUSED — FREE PLAN</BrTag>
                      )}
                    </div>
                    <BrTag bg={i % 2 === 0 ? p.accent : p.info} fg={i % 2 === 0 ? p.accentFg : p.infoFg}>{r.time_control.toUpperCase()}</BrTag>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 12, fontSize: 12, fontFamily: TYPE.mono, fontWeight: 700, letterSpacing: 0.5 }}>
                    <div>
                      <div style={{ color: p.fgMuted, fontSize: 9.5, textTransform: "uppercase", letterSpacing: 1, marginBottom: 3 }}>SITUATIONS</div>
                      <div style={{ lineHeight: 1.4 }}>
                        {r.situations.length === 0 ? "Any" : r.situations.map((s) => SITUATION_LABEL[s] ?? s).join(" · ")}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: p.fgMuted, fontSize: 9.5, textTransform: "uppercase", letterSpacing: 1, marginBottom: 3 }}>SOURCE MIX</div>
                      <div>{r.source_mix}% your · {100 - r.source_mix}% similar</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                    <BrBtn size="sm" variant="accent" onClick={() => startRoutine(r.id)} disabled={runRoutine.isPending || r.paused_by_tier} style={{ flex: 1, opacity: r.paused_by_tier ? 0.4 : 1 }}>Start session →</BrBtn>
                    <BrBtn size="sm" variant="surface" onClick={() => onOpenBuilder(r)}>Edit</BrBtn>
                  </div>
                </BrBox>
              ))}
            </div>
          ) : (
            <BrBox padding={isMobile ? 18 : 22} shadowSize={4}>
              <div style={{ fontSize: 13, color: p.fgMuted, fontWeight: 600 }}>
                No routines yet. Build a daily plan focused on the patterns, openings and formats you want to drill.
              </div>
            </BrBox>
          )}
        </div>

        <BrBox padding={isMobile ? 20 : 28} shadowSize={6}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
            <div>
              <BrLabel>ONE-OFF SESSION</BrLabel>
              <h2 style={{ fontFamily: TYPE.display, fontSize: isMobile ? 26 : 32, fontWeight: 800, letterSpacing: -0.8, textTransform: "uppercase", margin: "6px 0 0" }}>
                Pick situations to drill.
              </h2>
              <div style={{ fontSize: 12.5, color: p.fgMuted, marginTop: 6 }}>
                {picked.length} pattern{picked.length !== 1 ? "s" : ""} selected · we&rsquo;ll pull from your last 90 days of games.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <BrBtn size="sm" variant="ghost" withShadow={false} onClick={() => setPicked([])}>Clear</BrBtn>
              <BrBtn size="sm" variant="surface" onClick={() => setPicked([...ALL_SITUATIONS])}>All</BrBtn>
            </div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 18 }}>
            {ALL_SITUATIONS.map((s) => (
              <Chip key={s} label={s} active={picked.includes(s)} onClick={() => toggle(s)} />
            ))}
          </div>
          <div
            style={{
              paddingTop: 14,
              borderTop: `2px solid ${p.border}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div style={{ fontSize: 12, fontFamily: TYPE.mono, fontWeight: 700, color: p.fgMuted, textTransform: "uppercase", letterSpacing: 0.8 }}>
              Time control: <span style={{ color: p.fg }}>BLITZ 5+0</span> · 12 positions · ~18 min
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <BrBtn size="md" variant="surface" onClick={() => onOpenBuilder(null)}>Save as routine</BrBtn>
              <BrBtn size="md" variant="accent" onClick={() => startSession()} disabled={picked.length === 0 || createSession.isPending}>
                Start session →
              </BrBtn>
            </div>
          </div>
        </BrBox>

        <div>
          <BrLabel>BY OPENING</BrLabel>
          <h2 style={{ fontFamily: TYPE.display, fontSize: isMobile ? 26 : 32, fontWeight: 800, letterSpacing: -0.8, textTransform: "uppercase", margin: "6px 0 14px" }}>
            Drill positions from a specific system.
          </h2>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {ALL_OPENINGS.slice(0, 12).map((o) => (
              <button
                key={o}
                onClick={() => startSession()}
                style={{
                  padding: "10px 14px",
                  background: p.card,
                  color: p.fg,
                  border: `2px solid ${p.border}`,
                  boxShadow: offsetShadow(p, 2, 2),
                  fontFamily: TYPE.body,
                  fontWeight: 700,
                  fontSize: 13,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  cursor: "pointer",
                }}
              >
                {o} →
              </button>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
